import type { FC, ReactNode } from "react";
import { Header, Heading } from "react-aria-components";
import { twMerge } from "tailwind-merge";

/** Standard sizing for icons inside a chat header's action buttons. */
export const chatHeaderIconClassName = "size-4.5 shrink-0";

/** Standard sizing for the Alpharun mark when it leads a chat header's title. */
export const chatHeaderMarkClassName = "size-6 p-1.5";

export interface ChatHeaderProps {
  title: string;
  /** Leading decoration before the title (e.g. a clock icon, the Alpharun mark). */
  titleIcon?: ReactNode;
  /** Trailing decoration after the title (e.g. a "View only" chip). */
  titleAccessory?: ReactNode;
  /**
   * Right-aligned action cluster. A slot (not a config array) so surfaces can
   * mix plain icon buttons with menu and popover triggers.
   */
  actions?: ReactNode;
  className?: string;
}

/**
 * Header row for a chat surface: truncated title on the left, action cluster
 * on the right. Padding and borders are the surface's concern via `className`.
 */
export const ChatHeader: FC<ChatHeaderProps> = ({
  title,
  titleIcon,
  titleAccessory,
  actions,
  className,
}) => (
  <Header
    className={twMerge(
      "flex min-w-0 shrink-0 items-center justify-between gap-3",
      className,
    )}
  >
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {titleIcon}
      <Heading
        slot="title"
        className="min-w-0 truncate text-sm leading-5 font-medium text-foreground"
      >
        {title}
      </Heading>
      {titleAccessory}
    </div>

    {actions ? (
      <div className="flex shrink-0 items-center gap-0.5">{actions}</div>
    ) : null}
  </Header>
);
